import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { API, useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { ArrowLeft, Plus, Trash2, KeyRound, User as UserIcon, Wand2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";

export default function AdminUsersPage() {
  const nav = useNavigate();
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "standard" });
  const [resetting, setResetting] = useState(null);
  const [newPassword, setNewPassword] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/users`);
      setUsers(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const createUser = async () => {
    try {
      await axios.post(`${API}/users`, form);
      toast.success("User created");
      setCreating(false);
      setForm({ email: "", password: "", name: "", role: "standard" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };

  const changeRole = async (u, role) => {
    try {
      await axios.patch(`${API}/users/${u.id}`, { role });
      toast.success(`${u.email} is now ${role}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };

  const toggleFixFaces = async (u, next) => {
    try {
      await axios.patch(`${API}/users/${u.id}`, { can_fix_faces: next });
      toast.success(
        next
          ? `${u.email} can now use AI Fix Faces`
          : `AI Fix Faces disabled for ${u.email}`,
      );
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };

  const resetPassword = async () => {
    if (newPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    try {
      await axios.patch(`${API}/users/${resetting.id}`, { password: newPassword });
      toast.success("Password updated. Share it out-of-band with the user.");
      setResetting(null);
      setNewPassword("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Delete user ${u.email}?`)) return;
    try {
      await axios.delete(`${API}/users/${u.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="min-h-screen bg-background text-slate-900">
      <header className="border-b border-border bg-white/95 backdrop-blur sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button size="icon" variant="ghost" onClick={() => nav("/")}
              className="h-9 w-9 hover:bg-slate-100 rounded-md">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="font-heading font-bold text-lg tracking-tight">Users</h1>
          </div>
          <Button onClick={() => setCreating(true)}
            className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-md h-9 font-semibold shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-[transform,box-shadow]"
            data-testid="btn-new-user"
          >
            <Plus className="h-4 w-4 mr-1.5" />New user
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 lg:px-10 py-10">
        <div className="mb-8">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">Team</p>
          <h2 className="font-heading text-4xl font-black tracking-tighter">Team members</h2>
          <p className="text-slate-500 mt-2 font-medium">
            Create users, toggle their role, and reset passwords out-of-band.
          </p>
        </div>

        {loading ? (
          <div className="space-y-2">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-14 bg-slate-100 animate-pulse rounded-md" />
            ))}
          </div>
        ) : (
          <div className="bg-white border border-border rounded-md divide-y divide-border">
            {users.map((u) => (
              <div key={u.id} className="px-4 py-3 flex items-center gap-3" data-testid={`user-row-${u.email}`}>
                <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                  <UserIcon className="h-4 w-4 text-slate-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold truncate">{u.email}</span>
                    {u.name && <span className="text-xs text-slate-500 font-medium">· {u.name}</span>}
                    {u.id === me?.id && (
                      <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest bg-blue-50 text-blue-700 border border-blue-200">
                        You
                      </span>
                    )}
                  </div>
                  {u.created_at && (
                    <p className="text-xs text-slate-400 mt-0.5 font-medium">
                      Created {new Date(u.created_at).toLocaleString()}
                    </p>
                  )}
                </div>
                <Select
                  value={u.role}
                  onValueChange={(v) => changeRole(u, v)}
                  disabled={u.id === me?.id}
                >
                  <SelectTrigger
                    className="w-32 bg-slate-50 border-border rounded-md h-9 text-sm"
                    data-testid={`role-select-${u.email}`}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="standard">Standard</SelectItem>
                  </SelectContent>
                </Select>
                <label
                  className="hidden md:inline-flex items-center gap-2 px-3 h-9 rounded-md border border-border bg-slate-50 cursor-pointer"
                  title="Allow this user to use AI Fix Faces inside the analyzer"
                >
                  <Wand2 className="h-3.5 w-3.5 text-slate-500" />
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">
                    Fix Faces
                  </span>
                  <Switch
                    checked={!!u.can_fix_faces}
                    onCheckedChange={(v) => toggleFixFaces(u, v)}
                    data-testid={`fix-faces-switch-${u.email}`}
                  />
                </label>
                <Button
                  size="icon" variant="ghost"
                  onClick={() => { setResetting(u); setNewPassword(""); }}
                  className="h-9 w-9 text-slate-600 hover:text-primary hover:bg-primary/10 rounded-md"
                  title="Reset password"
                  data-testid={`reset-pwd-${u.email}`}
                >
                  <KeyRound className="h-4 w-4" />
                </Button>
                <Button
                  size="icon" variant="ghost"
                  onClick={() => deleteUser(u)}
                  disabled={u.id === me?.id}
                  className="h-9 w-9 text-slate-500 hover:text-destructive hover:bg-red-50 rounded-md disabled:opacity-30"
                  title="Delete user"
                  data-testid={`delete-user-${u.email}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Create user */}
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="bg-white border border-border rounded-md max-w-md text-slate-900">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-bold tracking-tight text-slate-900">Create user</DialogTitle>
            <DialogDescription className="text-slate-500">
              Set the initial password here and share it with the user out-of-band.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Email</Label>
              <Input type="email" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="editor@company.com"
                autoComplete="off"
                className="bg-white text-slate-900 placeholder:text-slate-400 border border-slate-300 rounded-md h-10 focus-visible:ring-2 focus-visible:ring-primary focus-visible:border-primary"
                data-testid="new-user-email"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Name (optional)</Label>
              <Input value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Jane Editor"
                autoComplete="off"
                className="bg-white text-slate-900 placeholder:text-slate-400 border border-slate-300 rounded-md h-10 focus-visible:ring-2 focus-visible:ring-primary focus-visible:border-primary"
                data-testid="new-user-name"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Password (min 8 chars)</Label>
              <Input type="text" value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="Give them a strong initial password"
                autoComplete="new-password"
                className="bg-white text-slate-900 placeholder:text-slate-400 border border-slate-300 rounded-md h-10 font-mono focus-visible:ring-2 focus-visible:ring-primary focus-visible:border-primary"
                data-testid="new-user-password"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="bg-white text-slate-900 border border-slate-300 rounded-md h-10 focus:ring-2 focus:ring-primary" data-testid="new-user-role">
                  <SelectValue placeholder="Pick a role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreating(false)}
              className="text-slate-700 hover:bg-slate-100 rounded-md">
              Cancel
            </Button>
            <Button
              onClick={createUser}
              disabled={!form.email || form.password.length < 8}
              className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-md font-semibold"
              data-testid="new-user-submit"
            >
              Create user
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password */}
      <Dialog open={!!resetting} onOpenChange={(o) => { if (!o) setResetting(null); }}>
        <DialogContent className="bg-white border border-border rounded-md max-w-sm text-slate-900">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-bold tracking-tight text-slate-900">
              Reset password
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              For {resetting?.email}. Existing sessions stay valid until they expire.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 mt-2">
            <Label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">New password (min 8 chars)</Label>
            <Input type="text" value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="At least 8 characters"
              autoComplete="new-password"
              className="bg-white text-slate-900 placeholder:text-slate-400 border border-slate-300 rounded-md h-10 font-mono focus-visible:ring-2 focus-visible:ring-primary focus-visible:border-primary"
              data-testid="reset-password-input"
            />
            <p className="text-xs text-slate-500">
              Share this with the user out-of-band (Signal, phone, in person).
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setResetting(null)}
              className="text-slate-700 hover:bg-slate-100 rounded-md">
              Cancel
            </Button>
            <Button
              onClick={resetPassword}
              disabled={newPassword.length < 8}
              className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-md font-semibold"
              data-testid="reset-password-submit"
            >
              Set password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
